import { Cron } from "croner";

import type { Workflow, WorkflowRun, WorkflowScheduleEntry } from "./workflows.types";

/**
 * Whether a workflow can execute right now.
 *
 * All workflows require internet connectivity. When offline, everything is paused.
 */
export function workflowCanRun(_workflow: Workflow, isOnline: boolean): boolean {
	return isOnline;
}

/**
 * Whether a workflow has missed at least one scheduled cron tick since its last run
 * (or since creation, if it has never run).
 *
 * Computes the next scheduled tick after the baseline time. If that tick is in the past
 * relative to `now`, at least one tick was missed.
 */
export function workflowIsDue(workflow: Workflow, lastRunTime: Date | null, now: Date): boolean {
	if (!workflow.enabled) {
		return false;
	}

	const baseline = lastRunTime ?? new Date(workflow.createdAt);

	try {
		const cron = new Cron(workflow.schedule);
		const nextAfterBaseline = cron.nextRun(baseline);
		if (!nextAfterBaseline) {
			return false;
		}
		return nextAfterBaseline.getTime() <= now.getTime();
	} catch {
		// Invalid cron expression
		return false;
	}
}

/**
 * Compute the full schedule state for a list of workflows.
 *
 * Pure function — takes raw data, returns derived state. No atoms, no side effects.
 * This is the single source of truth for "what's the scheduling state of each workflow?"
 *
 * `lastScheduledAt` is derived from the most recent run's `startedAt` — no separate
 * field stored on the workflow.
 */
export function computeWorkflowSchedule(
	workflows: Workflow[],
	runs: WorkflowRun[],
	isOnline: boolean,
	now: Date
): WorkflowScheduleEntry[] {
	// Index runs by workflow ID, pre-sorted newest-first (runs come in that order from cache)
	const runsByWorkflow = new Map<string, WorkflowRun[]>();
	for (const run of runs) {
		const list = runsByWorkflow.get(run.automationId);
		if (list) {
			list.push(run);
		} else {
			runsByWorkflow.set(run.automationId, [run]);
		}
	}

	return workflows.map((workflow) => {
		const wfRuns = runsByWorkflow.get(workflow.id) ?? [];
		const latestRun = wfRuns[0]; // newest first
		const lastRunTime = latestRun ? new Date(latestRun.startedAt) : null;

		const canRun = workflowCanRun(workflow, isOnline);
		const isDue = workflowIsDue(workflow, lastRunTime, now);

		const status: WorkflowScheduleEntry["status"] =
			latestRun?.status === "running" || latestRun?.status === "pending" ? "running" : "idle";

		const lastRunStatus: WorkflowScheduleEntry["lastRunStatus"] =
			latestRun?.status === "completed" ||
			latestRun?.status === "failed" ||
			latestRun?.status === "lost"
				? latestRun.status
				: undefined;

		const scheduleStatus: WorkflowScheduleEntry["scheduleStatus"] = !workflow.enabled
			? "disabled"
			: canRun
				? "active"
				: "paused";

		let nextRun: Date | null = null;
		try {
			const cron = new Cron(workflow.schedule);
			nextRun = cron.nextRun(now);
		} catch {
			// Invalid cron expression
		}

		return {
			workflow,
			latestRun,
			nextRun,
			status,
			lastRunStatus,
			scheduleStatus,
			isDue,
		};
	});
}
